// GEL BÊTA le 07/10/2026. Voir docs/GEL_BETA.md.
// Architecture figée : paiement dual, Coco via settings, rôles user_roles/has_role, âge 18 soft, pas de Connect.
// Porte ouverte : corrections de retours testeurs uniquement sur ordre Hiti, lot unique, sans rouvrir ces règles.
// Interdit sans Hiti : tama-wip→main, édition client.ts / types.ts / .env / config.toml / previewAuthStorage.
import { bannedWordOr } from "@/lib/bannedWords";
import { profilePath } from "@/lib/profilePath";
import { useState, useEffect, useRef, useCallback, lazy, Suspense } from "react";
// Panneau d'emojis chargé à part : le fil ne l'embarque pas.
const CommentEmojiButton = lazy(() => import("@/components/comments/CommentEmojiButton"));

import { supabase } from "@/integrations/supabase/client";
import type { PlaybackState } from "@/services/playback/engineApi";
import { usePlaybackEngine } from "@/services/playback/engineContext";
import {
  isAudioUnlocked,
  getAudioUnlockTime,
  onAudioUnlocked,
  markAudioUnlocked,
} from "@/services/playback/audioSession";

import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import { useBrowserDetection } from "@/hooks/useBrowserDetection";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { getPlayerProgress, playerProgressCssVars } from "@/services/playback/playerProgress";

import { useToast } from "@/hooks/use-toast";
import { Heart, MessageCircle, Share2, Bookmark, Send, DollarSign, PlayCircle, Zap, X, Hash, Pencil, Flag, ChevronDown, Plus, MoreVertical, Trash2, Smile } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ReportDialog } from "./ReportDialog";
import VideoBoostDialog from "./VideoBoostDialog";
import VideoEditDialog from "./VideoEditDialog";


import { Badge } from "@/components/ui/badge";
import { Video } from "@/types/videos";
import { Comment } from "@/types/comments";

import { Loader2 } from "lucide-react";
import { logError } from "@/services/errorLogger";
import { useSecuritySettings } from "@/hooks/useSecuritySettings";
import VideoProductCta from "@/components/player/VideoProductCta";
import { getShareOrigin } from "@/lib/shareOrigin";
import { resolvePlaybackUrls } from "@/services/playbackUrls";
import { tagButtonClassName, tagChipClassName } from "@/lib/tagChips";


/**
 * Ce composant est de l'UI PURE : aucune instance HLS, aucun `play()` ni
 * `muted` piloté ici. Tout passe par le moteur fourni par le conteneur, avec
 * une politique son unique pour la session. Le `<video>` appartient à cette
 * diapositive et n'est jamais déplacé ailleurs.
 */

interface VideoPlayerProps {
  video: Video;
  onRefresh?: () => void;
  isActive?: boolean;
  tagName?: string;
  onCommentsOpenChange?: (open: boolean) => void;
  index?: number;
  totalVideos?: number;
}

/*
 * Texte « activer le son » : libellé choisi une fois au chargement du fichier,
 * et drapeau de session levé au premier geste (onAudioUnlocked). Aucun calcul
 * au rendu : la bulle est présente dès le premier affichage de la carte.
 */
const SOUND_HINT_LABEL =
  typeof window !== "undefined" && "ontouchstart" in window
    ? "Touches pour activer le son"
    : "Cliques pour activer le son";
let soundHintDismissed = false;
onAudioUnlocked(() => {
  soundHintDismissed = true;
});
const VideoPlayer = ({
  video,
  onRefresh,
  isActive = false,
  tagName,
  onCommentsOpenChange,
  index = 0,
  totalVideos = 1,
}: VideoPlayerProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { data: securitySettings } = useSecuritySettings();
  const commentAutoModeration = securitySettings?.content_moderation?.auto_content_moderation_enabled === true;
  const commentStrictMode = securitySettings?.content_moderation?.strict_mode === true;

  /** Pré-vérification IA d'un commentaire (sans écriture). Renvoie false si refusé. */
  const precheckComment = async (text: string): Promise<boolean> => {
    const { data, error } = await supabase.functions.invoke("moderate-content", {
      body: { content_type: "comment", text },
    });
    if (error) {
      logError("Comment precheck error", error as Error, { component: "VideoPlayer", action: "comment" });
      return true; // Le serveur tranchera au second appel ; le commentaire reste en attente.
    }
    const status = (data as { status?: string } | null)?.status;
    if (status === "blocked" || (status === "warning" && commentStrictMode)) {
      toast({ variant: "destructive", title: "Commentaire refusé", description: "Ce commentaire ne respecte pas nos règles." });
      return false;
    }
    return true;
  };

  /** Second appel : le serveur écrit le verdict. Renvoie le statut final, ou null en cas d'échec. */
  const finalizeCommentModeration = async (commentId: string, text: string): Promise<string | null> => {
    const { data, error } = await supabase.functions.invoke("moderate-content", {
      body: { content_type: "comment", content_id: commentId, text },
    });
    if (error) {
      logError("Comment moderation write error", error as Error, { component: "VideoPlayer", action: "comment" });
      toast({ title: "Commentaire envoyé — vérification en cours avant publication." });
      return null;
    }
    const status = (data as { status?: string } | null)?.status;
    return status === "blocked" ? "blocked" : status === "warning" ? "pending" : "approved";
  };
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  /**
   * Moteur de lecture du conteneur qui héberge ce lecteur. Sans fournisseur,
   * c'est le moteur historique : le feed actuel garde donc son comportement.
   */
  const playbackEngine = usePlaybackEngine();
  /** Élément média propre à cette slide, jamais déplacé vers une autre slide. */
  const mediaRef = useRef<HTMLVideoElement | null>(null);
  // iPhone : l'attribut HTML muted="" doit exister dès la création de la balise
  // (React ne pose que la propriété). Sans lui, WebKit refuse le départ muet.
  const attachMedia = useCallback((el: HTMLVideoElement | null) => {
    if (el) {
      el.setAttribute("muted", "");
      el.defaultMuted = true;
      // Après déverrouillage, ne pas remettre en muet une balise remontée.
      if (!isAudioUnlocked()) el.muted = true;
    }
    mediaRef.current = el;
  }, []);

  const [isLiked, setIsLiked] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [commentsCount, setCommentsCount] = useState(0);
  const [sharesCount, setSharesCount] = useState(0);
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState("");
  const [sendingComment, setSendingComment] = useState(false);
  const commentInputRef = useRef<HTMLInputElement>(null);
  const editCommentInputRef = useRef<HTMLInputElement>(null);
  const [showComments, setShowComments] = useState(false);
  
  const [isPlaying, setIsPlaying] = useState(false);
  const [pausedByUser, setPausedByUser] = useState(false);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  // Texte éphémère « activer le son » : affiché tant que le son de la session
  // n'est pas déverrouillé, jamais réaffiché ensuite.
  const [audioUnlocked, setAudioUnlocked] = useState(soundHintDismissed);
  useEffect(() => onAudioUnlocked(() => setAudioUnlocked(true)), []);
  const [isFollowing, setIsFollowing] = useState(false);
  const [showBoostSection, setShowBoostSection] = useState(false);
  const [showEditSection, setShowEditSection] = useState(false);
  const [showProductCta, setShowProductCta] = useState(false);
  const [isOwner, setIsOwner] = useState(false);

  // État de lecture reçu du moteur (aucune source gérée ici).
  const [playbackLoading, setPlaybackLoading] = useState(true);
  const [playbackError, setPlaybackError] = useState<string | null>(null);

  // Edit section states

  // Boost : l'achat est délégué à VideoBoostDialog (RPC serveur).

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showTopBar, setShowTopBar] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [showReportDialog, setShowReportDialog] = useState(false);
  // Actions sur un commentaire : édition en place, suppression, signalement.
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingCommentContent, setEditingCommentContent] = useState("");
  const [savingComment, setSavingComment] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState<string | null>(null);
  const [commentToReport, setCommentToReport] = useState<string | null>(null);

  const [showCommentsSection, setShowCommentsSection] = useState(false);
  const [commentLikes, setCommentLikes] = useState<Record<string, { isLiked: boolean; count: number }>>({});
  const [showExpandedDescription, setShowExpandedDescription] = useState(false);

  /**
   * Marge basse commune à toutes les commandes (barre de nav mobile + zone sûre iOS).
   * Aucun ajustement par navigateur : la variable CSS suit la zone réellement visible,
   * donc le rendu est identique sur Safari, Chrome, Firefox, Edge, Android et bureau.
   */
  const bottomInset = "var(--player-bottom-inset)";
  // Réglages administrables de la barre d'avancement (couloir, piste, pouce).
  const progressSettings = getPlayerProgress();
  const progressLaneHeight = `${progressSettings.laneHeightRem}rem`;

  useEffect(() => {
    if (user) {
      checkInteractions();
      setIsOwner(user.id === video.profiles.id);
      // Enregistrer la vue de manière unique
      recordView();
    }
    fetchCounts();
    fetchComments();
  }, [video.id, user]);
  const recordView = async () => {
    if (!user) return;
    try {
      // Insérer une vue directement (la contrainte unique empêchera les doublons)
      await supabase.from("views" as any).insert({
        video_id: video.id,
        user_id: user.id,
      });
    } catch (error) {
      // Ignorer silencieusement les erreurs de doublons
    }
  };

  /** Une source de repli existe (fichier d'origine) : pas d'écran « traitement ». */
  const hasFallbackSource = !!(video.legacy_video_url || video.video_url);

  /**

   * Branchement au moteur de lecture : chargement à la demande.
   * On ne charge QUE la vidéo à l'écran ; aucune voisine n'est préparée.
   * Le jeton Bunny est signé ici, au moment où la diapositive devient active
   * (le moteur écoute le cache et reprend l'URL fraîche dès qu'elle arrive).
   *
   * IMPORTANT : aucun `detach` ici. Sur WebKit (Safari/Chrome iOS), sortir puis
   * remettre le `<video>` HLS natif du DOM fait perdre le décodeur.
   */
  useEffect(() => {
    const media = mediaRef.current;
    if (!isActive) return;
    void resolvePlaybackUrls([video.id]);
    playbackEngine.setActive(video, media, index);
    // Volontairement limité : un simple re-render ne doit rien recharger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, video.id, index]);


  /**
   * Vrai démontage : la diapositive quitte la fenêtre de rendu. On range alors
   * l'élément du pool, sans jamais le détruire.
   *
   * L'id courant passe par une ref : avec `video.id` en dépendance, le nettoyage
   * partait à CHAQUE changement de vidéo et vidait le décodeur de la carte
   * recyclée juste avant son préchauffage.
   */
  const currentVideoIdRef = useRef(video.id);
  currentVideoIdRef.current = video.id;

  useEffect(() => {
    return () => {
      playbackEngine.detach(currentVideoIdRef.current);
    };
  }, [playbackEngine]);


  /** État de lecture (barre d'avancement, bouton lecture) fourni par le moteur. */
  useEffect(() => {
    const apply = (state: PlaybackState) => {
      setCurrentTime(state.currentTime);
      setDuration(state.duration);
      setIsPlaying(state.isPlaying);
      setPlaybackLoading(state.isWaiting);
      setPlaybackError(state.error);
      setAutoplayBlocked(state.autoplayBlocked);
      setIsMuted(state.isMuted);
    };
    const unsubscribe = playbackEngine.subscribe(video.id, apply);
    return unsubscribe;
  }, [video.id, playbackEngine]);

  /**
   * Réinitialise l'indicateur de pause utilisateur à chaque changement de
   * diapositive (swipe) ou de vidéo, afin que le PlayCircle ne s'affiche pas
   * pendant le chargement / autoplay d'une nouvelle vidéo.
   */
  useEffect(() => {
    setPausedByUser(false);
  }, [video.id, isActive]);

  // Handle orientation change
  useEffect(() => {
    const handleOrientationChange = () => {
      if (window.orientation === 90 || window.orientation === -90) {
        enterFullscreen();
      } else {
        exitFullscreen();
      }
    };
    window.addEventListener("orientationchange", handleOrientationChange);
    return () => window.removeEventListener("orientationchange", handleOrientationChange);
  }, []);

  // Handle scroll to hide/show top bar on mobile
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY && currentScrollY > 50) {
        setShowTopBar(false);
      } else {
        setShowTopBar(true);
      }
      setLastScrollY(currentScrollY);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true,
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  const enterFullscreen = () => {
    const el = playbackEngine.getElement(video.id);
    if (el?.requestFullscreen) {
      el.requestFullscreen();
    }
    setIsFullscreen(true);
  };

  const exitFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    }
    setIsFullscreen(false);
  };
  const checkInteractions = async () => {
    if (!user) return;

    // Check like
    const { data: likeData } = await supabase
      .from("likes")
      .select("id")
      .eq("video_id", video.id)
      .eq("user_id", user.id)
      .single();
    setIsLiked(!!likeData);

    // Check saved
    const { data: savedData } = await supabase
      .from("saved_videos")
      .select("id")
      .eq("video_id", video.id)
      .eq("user_id", user.id)
      .single();
    setIsSaved(!!savedData);

    // Check following
    const { data: followData } = await supabase
      .from("follows")
      .select("id")
      .eq("follower_id", user.id)
      .eq("following_id", video.profiles.id)
      .single();
    setIsFollowing(!!followData);
  };
  const fetchCounts = async () => {
    const { count: likes } = await supabase
      .from("likes")
      .select("*", {
        count: "exact",
        head: true,
      })
      .eq("video_id", video.id);
    setLikesCount(likes || 0);
    const { count: comments } = await supabase
      .from("comments")
      .select("*", {
        count: "exact",
        head: true,
      })
      .eq("video_id", video.id);
    setCommentsCount(comments || 0);
    // Le compteur de partages vient du total agrégé sur la vidéo :
    // la table `shares` est protégée par RLS (chacun ne voit que ses lignes),
    // un count direct renverrait donc 1 au lieu du total réel.
    await refreshSharesCount();
  };

  /** Lit le total public de partages (trigger `update_video_shares_count`). */
  const refreshSharesCount = async () => {
    const { data } = await supabase
      .from("videos")
      .select("shares_count")
      .eq("id", video.id)
      .maybeSingle();
    setSharesCount(data?.shares_count ?? video.shares_count ?? 0);
  };

  const fetchComments = async () => {
    // Filet de diffusion : commentaires approuvés, plus les siens quand on est connecté.
    let query = supabase
      .from("comments")
      .select(
        `
        *,
        profiles:user_id (
          id,
          full_name,
          username,
          avatar_url
        )
      `,
      )
      .eq("video_id", video.id);

    query = user
      ? query.or(`moderation_status.eq.approved,user_id.eq.${user.id}`)
      : query.eq("moderation_status", "approved");

    const { data } = await query.order("created_at", {
      ascending: false,
    });
    if (data) {
      setComments(data);
      fetchCommentLikes(data.map((c) => c.id));
    }
  };
  const fetchCommentLikes = async (commentIds: string[]) => {
    if (!user || commentIds.length === 0) return;
    const { data: likesData } = await supabase
      .from("comment_likes")
      .select("comment_id")
      .eq("user_id", user.id)
      .in("comment_id", commentIds);
    const { data: countsData } = await supabase.from("comment_likes").select("comment_id").in("comment_id", commentIds);
    const likesMap: Record<
      string,
      {
        isLiked: boolean;
        count: number;
      }
    > = {};
    commentIds.forEach((id) => {
      const isLiked = likesData?.some((l) => l.comment_id === id) || false;
      const count = countsData?.filter((l) => l.comment_id === id).length || 0;
      likesMap[id] = {
        isLiked,
        count,
      };
    });
    setCommentLikes(likesMap);
  };
  const toggleCommentLike = async (commentId: string) => {
    if (!user) return;
    const currentLike = commentLikes[commentId];
    if (currentLike?.isLiked) {
      await supabase.from("comment_likes").delete().eq("comment_id", commentId).eq("user_id", user.id);
      setCommentLikes((prev) => ({
        ...prev,
        [commentId]: {
          isLiked: false,
          count: Math.max(0, prev[commentId].count - 1),
        },
      }));
    } else {
      await supabase.from("comment_likes").insert({
        comment_id: commentId,
        user_id: user.id,
      });
      setCommentLikes((prev) => ({
        ...prev,
        [commentId]: {
          isLiked: true,
          count: (prev[commentId]?.count || 0) + 1,
        },
      }));
    }
  };
  const toggleLike = async () => {
    if (!user) return;
    if (isLiked) {
      await supabase.from("likes").delete().eq("video_id", video.id).eq("user_id", user.id);
      setLikesCount((prev) => prev - 1);
    } else {
      await supabase.from("likes").insert({
        video_id: video.id,
        user_id: user.id,
      });
      setLikesCount((prev) => prev + 1);
    }
    setIsLiked(!isLiked);
  };
  const toggleSave = async () => {
    if (!user) return;
    if (isSaved) {
      await supabase.from("saved_videos").delete().eq("video_id", video.id).eq("user_id", user.id);
    } else {
      await supabase.from("saved_videos").insert({
        video_id: video.id,
        user_id: user.id,
      });
    }
    setIsSaved(!isSaved);
    toast({
      title: isSaved ? "Vidéo retirée" : "Vidéo enregistrée",
      description: isSaved ? "La vidéo a été retirée de vos favoris" : "La vidéo a été ajoutée à vos favoris",
    });
  };
  // Un seul lien pour tous les partages : l'URL lisible servie par le Worker OG.
  const shareUrl = `${getShareOrigin()}/video/${video.slug || video.id}`;

  const handleShare = async () => {
    // Compteur : une ligne par compte au maximum (contrainte unique).
    // Doublon = ignoré, la feuille s'ouvre quand même.
    if (user) {
      try {
        await supabase.from("shares").insert({
          video_id: video.id,
          user_id: user.id,
        });
      } catch {
        // Doublon : rien à signaler.
      }
      await refreshSharesCount();
    }
    // Feuille native du système (mobile ET ordinateur quand elle existe).
    if (typeof navigator.share === "function") {
      try {
        await navigator.share({
          title: video.title || "Vidéo Tama",
          text: video.title || "Découvrez cette vidéo sur Tama",
          url: shareUrl,
        });
      } catch {
        // Annulation (AbortError) : silencieux, pas de toast.
      }
      return;
    }
    // Repli unique : presse-papier.
    await navigator.clipboard.writeText(shareUrl);
    toast({
      title: "Lien copié",
      description: "Colle-le où tu veux",
    });
  };

  const handleComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !newComment.trim() || sendingComment) return;
    setSendingComment(true);
    try {
      await submitComment();
    } finally {
      setSendingComment(false);
    }
  };

  /** Envoi d'un commentaire : limite horaire, précontrôle, écriture puis verdict. */
  const submitComment = async () => {
    if (!user) return;
    // Check rate limit (20 comments/hour)
    try {
      const { data: rateLimitData, error: rateLimitError } = await supabase.functions.invoke("check-rate-limit", {
        body: {
          operationType: "comment_post",
          userId: user.id,
        },
      });
      if (rateLimitError || !rateLimitData?.allowed) {
        const resetAt = rateLimitData?.resetAt ? new Date(rateLimitData.resetAt) : null;
        const minutesLeft = resetAt ? Math.ceil((resetAt.getTime() - Date.now()) / 60000) : 60;
        toast({
          variant: "destructive",
          title: "Limite atteinte",
          description: `Vous avez atteint la limite de 20 commentaires par heure. Réessayez dans ${minutesLeft} minutes.`,
        });
        return;
      }
    } catch (error) {
      logError("Rate limit check error", error as Error, {
        component: "VideoPlayer",
        action: "comment",
      });
    }
    const text = newComment.trim();
    if (commentAutoModeration && !(await precheckComment(text))) return;

    const { data: inserted, error } = await supabase
      .from("comments")
      .insert({ video_id: video.id, user_id: user.id, content: text })
      .select("id")
      .single();
    if (error) {
      logError("Comment insert error", error as Error, { component: "VideoPlayer", action: "comment" });
      toast({ variant: "destructive", title: "Envoi impossible", description: bannedWordOr(error, "Votre commentaire n'a pas pu être envoyé. Réessayez.") });
      return;
    }
    setNewComment("");
    if (commentAutoModeration && inserted) {
      const finalStatus = await finalizeCommentModeration(inserted.id, text);
      fetchComments();
      if (finalStatus === "approved") {
        setCommentsCount((prev) => prev + 1);
        toast({ title: "Commentaire ajouté" });
      } else if (finalStatus === "blocked") {
        toast({ variant: "destructive", title: "Commentaire refusé", description: "Ce commentaire ne respecte pas nos règles." });
      } else if (finalStatus === "pending") {
        toast({ title: "Commentaire envoyé — vérification en cours avant publication." });
      }
      return;
    }
    fetchComments();
    setCommentsCount((prev) => prev + 1);
    toast({ title: "Commentaire ajouté" });
  };
  /** Passe un commentaire en édition (seul son auteur y a accès). */
  const startEditComment = (commentId: string, content: string) => {
    setEditingCommentId(commentId);
    setEditingCommentContent(content);
  };

  const cancelEditComment = () => {
    setEditingCommentId(null);
    setEditingCommentContent("");
  };

  /** Enregistre la modification : rien n'est écrit si le texte est vide ou inchangé. */
  const saveEditedComment = async (commentId: string) => {
    if (!user) return;
    const original = comments.find((c) => c.id === commentId)?.content ?? "";
    const content = editingCommentContent.trim();
    if (!content || content === original.trim()) {
      cancelEditComment();
      return;
    }

    setSavingComment(true);
    if (commentAutoModeration && !(await precheckComment(content))) {
      setSavingComment(false);
      return;
    }
    const { error } = await supabase
      .from("comments")
      .update({ content })
      .eq("id", commentId)
      .eq("user_id", user.id);

    if (error) {
      setSavingComment(false);
      toast({
        variant: "destructive",
        title: "Modification impossible",
        description: bannedWordOr(error, "Votre commentaire n'a pas pu être modifié. Réessayez."),
      });
      return;
    }

    setComments((prev) => prev.map((c) => (c.id === commentId ? { ...c, content } : c)));
    if (commentAutoModeration) {
      const finalStatus = await finalizeCommentModeration(commentId, content);
      setSavingComment(false);
      cancelEditComment();
      if (finalStatus !== "approved") {
        // Le commentaire sort du décompte public ; on relit la liste et le compteur serveur.
        fetchComments();
        const { data: v } = await supabase.from("videos").select("comments_count").eq("id", video.id).maybeSingle();
        if (typeof v?.comments_count === "number") setCommentsCount(v.comments_count);
        if (finalStatus === "blocked") {
          toast({ variant: "destructive", title: "Commentaire refusé", description: "Ce commentaire ne respecte pas nos règles." });
        } else if (finalStatus === "pending") {
          toast({ title: "Commentaire envoyé — vérification en cours avant publication." });
        }
        return;
      }
    } else {
      setSavingComment(false);
      cancelEditComment();
    }
    toast({ title: "Commentaire modifié" });
  };

  /** Supprime définitivement un commentaire de son auteur. */
  const deleteComment = async (commentId: string) => {
    if (!user) return;
    const { error } = await supabase.from("comments").delete().eq("id", commentId).eq("user_id", user.id);
    if (error) {
      toast({
        variant: "destructive",
        title: "Suppression impossible",
        description: "Votre commentaire n'a pas pu être supprimé. Réessayez.",
      });
      return;
    }
    setComments((prev) => prev.filter((c) => c.id !== commentId));
    setCommentsCount((prev) => Math.max(0, prev - 1));
    if (editingCommentId === commentId) cancelEditComment();
    toast({ title: "Commentaire supprimé" });
  };

  const followFromPlayer = async () => {

    if (!user || user.id === video.profiles.id || isFollowing) return;
    const { error } = await supabase.from("follows").insert({
      follower_id: user.id,
      following_id: video.profiles.id,
    });
    if (error) {
      toast({
        title: "Erreur",
        description: "Impossible de suivre cet utilisateur",
        variant: "destructive",
      });
      return;
    }
    setIsFollowing(true);
  };

  const togglePlay = () => {
    markAudioUnlocked();
    // Premier geste de la session : le son vient de se déverrouiller dans ce
    // même tap (écouteur en capture). Pauser ici serait ressenti comme un bug.
    const unlockTime = getAudioUnlockTime();
    const justUnlocked =
      !audioUnlocked ||
      (unlockTime !== null && performance.now() - unlockTime < 800);
    if (isPlaying && justUnlocked) {
      playbackEngine.play(video.id);
      setPausedByUser(false);
      return;
    }
    if (isPlaying) {
      playbackEngine.pause(video.id);
      setPausedByUser(true);
    } else {
      playbackEngine.play(video.id);
      setPausedByUser(false);
    }
    setIsPlaying(!isPlaying);
  };
  const handleSeek = (value: number[]) => {
    playbackEngine.seek(video.id, value[0]);
    setCurrentTime(value[0]);
  };

  const openCommentsSection = () => setShowCommentsSection(true);
  const closeCommentsSection = () => setShowCommentsSection(false);
  const toggleCommentsSection = () => setShowCommentsSection((prev) => !prev);

  /**
   * Une section ouverte (commentaires, partage, édition, boost) fige la vidéo
   * courante. Le verrouillage est délégué au conteneur du feed via
   * `onCommentsOpenChange` : plus aucune manipulation du DOM global
   * (`document.body`, `querySelector`) qui cassait le scroll des panneaux.
   */
  const isSectionOpen = showCommentsSection || showEditSection || showBoostSection || showProductCta;
  useEffect(() => {
    onCommentsOpenChange?.(isSectionOpen);
    // Démontage avec un panneau ouvert : on relâche le verrou du feed.
    return () => {
      if (isSectionOpen) onCommentsOpenChange?.(false);
    };
  }, [isSectionOpen, onCommentsOpenChange]);

  return (
    <div className="h-full w-full bg-background relative">

      {/* Video container - fullscreen */}
      <div
        className={`relative w-full bg-player-background transition-all duration-300 ${showCommentsSection ? "h-[30vh]" : "h-full"}`}
      >
        {/*
          Chaque slide possède son élément média. Le moteur ne déplace jamais
          ce nœud et limite à deux les sources HLS attachées simultanément.
        */}
        <video
          ref={attachMedia}
          className="absolute inset-0 w-full h-full object-cover"
          muted
          playsInline
          preload="auto"
        />

        {/* Overlay de traitement / erreur : masqué si une source de repli est lisible */}
        {((playbackLoading && !hasFallbackSource) ||
          playbackError ||
          (!hasFallbackSource &&
            (video.processing_status === "pending" || video.processing_status === "processing"))) && (

          <div className="absolute inset-0 flex flex-col items-center justify-center bg-player-overlay/70 z-20 pointer-events-none">
            {!playbackError ? (
              <>
                <Loader2 className="h-10 w-10 text-player-foreground animate-spin mb-3" />
                <p className="text-player-foreground text-sm font-medium">Traitement de la vidéo…</p>
              </>
            ) : (
              <p className="text-player-foreground text-sm font-medium px-4 text-center">{playbackError}</p>
            )}
          </div>
        )}

        <button
          data-swipe-passthrough
          onClick={togglePlay}
          className={`absolute inset-0 flex items-center justify-center bg-transparent transition-opacity duration-300 ${showCommentsSection ? "opacity-0 pointer-events-none" : "opacity-100"}`}
        >
          {pausedByUser && !isPlaying && <PlayCircle className="h-20 w-20 text-player-foreground drop-shadow-lg" />}
        </button>

        {/* Incitation éphémère : disparaît au premier geste (son déverrouillé).
            pointer-events-none : le tap atteint le bouton play ci-dessus. */}
        {isActive && !audioUnlocked && !showCommentsSection && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
            <p className="bg-player-overlay/40 text-player-foreground text-sm font-medium px-4 py-2 rounded-full">
              {SOUND_HINT_LABEL}
            </p>
          </div>
        )}

        {/* Overlay UI - absolute positioning */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Top info - hide on mobile scroll */}
          {video.is_monetized && video.price && (
            <div
              className={`absolute top-4 right-4 bg-accent text-accent-foreground px-3 py-1 rounded-full flex items-center gap-1 pointer-events-auto transition-transform duration-300 ${!showTopBar ? "md:translate-y-0 -translate-y-20" : "translate-y-0"}`}
            >
              <DollarSign className="h-4 w-4" />
              <span className="font-semibold">{video.price} XPF</span>
            </div>
          )}

          {/* Bottom left info overlay - la marge droite réserve la voie des boutons d'action */}
          <div
            className={`absolute bottom-0 left-0 right-0 px-4 pt-2 pr-20 md:pr-24 bg-gradient-to-t from-player-background/80 via-transparent to-transparent pointer-events-auto transition-opacity duration-300 ${showCommentsSection ? "opacity-0 pointer-events-none" : "opacity-100"}`}
            style={{
              paddingBottom: `calc(${progressLaneHeight} + ${bottomInset})`,
            }}
          >
            {/* User info - une seule ligne compacte */}
            <div className="flex items-center gap-2 mb-1 min-w-0">
              <Avatar
                className="border-2 border-player-foreground cursor-pointer h-8 w-8 shrink-0"
                onClick={() => (window.location.href = profilePath(video.profiles))}
              >
                <AvatarImage src={video.profiles.avatar_url || undefined} />
                <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                  {video.profiles.full_name?.[0] || video.profiles.username?.[0] || "U"}
                </AvatarFallback>
              </Avatar>
              <div className="flex items-center gap-1.5 min-w-0 flex-1">
                <p className="text-sm font-semibold text-player-foreground truncate">
                  {video.profiles.full_name || "Utilisateur"}
                </p>
              </div>
              {user && user.id !== video.profiles.id && !isFollowing && (
                <button
                  onClick={followFromPlayer}
                  aria-label="Suivre"
                  className="flex items-center justify-center shrink-0 ml-1 h-7 w-7 rounded-md border border-player-border bg-player-surface/10 text-player-foreground hover:bg-player-surface/20"
                >
                  <Plus className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Exception layout : ligne CTA produit lié, entre l'auteur et le titre. */}
            {video.product_id && (
              <VideoProductCta
                productId={video.product_id}
                videoUserId={video.user_id}
                isOwner={user?.id === video.user_id}
                onOpenChange={setShowProductCta}
              />
            )}

            {/* Exception layout : mention « Contenu IA », après le CTA produit, au-dessus du titre. */}
            {video.is_ai_generated === true && (
              <div
                onClick={(e) => e.stopPropagation()}
                className="mb-0.5 inline-flex items-center rounded-md border border-player-border bg-player-surface/10 px-1.5 py-0.5 text-xs text-player-foreground"
              >
                Contenu IA
              </div>
            )}

            {video.title && <p className="text-sm font-semibold line-clamp-1 mb-0.5 text-player-foreground">{video.title}</p>}
            {video.description && (
              <div className="mb-1">
                {(() => {
                  const words = video.description.split(/\s+/);
                  const isLong = words.length > 8;
                  const truncated = isLong ? words.slice(0, 8).join(' ') : video.description;
                  return (
                    <p className="text-xs text-player-foreground/90 line-clamp-2">
                      {truncated}
                      {isLong && (
                        <button
                          onClick={() => setShowExpandedDescription(true)}
                          className="ml-1 text-player-foreground font-medium hover:underline"
                        >
                          ... voir plus
                        </button>
                      )}
                    </p>
                  );
                })()}
              </div>
            )}

            {/* Tags - intégrés en ligne de texte scrollable */}
            {video.tags && video.tags.length > 0 && (
              <div className="flex flex-nowrap gap-2 overflow-x-auto scrollbar-hide">
                {video.tags.map((tag) => (
                  <button
                    key={tag.id}
                    onClick={() => navigate(`/search?q=${encodeURIComponent(tag.name)}`)}
                    className={tagButtonClassName(
                      tag,
                      "text-xs text-player-foreground/80 hover:text-player-foreground whitespace-nowrap shrink-0",
                    )}
                  >
                    #{tag.name}
                  </button>
                ))}
              </div>
            )}

          </div>

          {/* Bande dédiée à la barre d'avancement : toujours entièrement visible,
              ancrée au-dessus de la barre de navigation et de la zone sûre iOS. */}
          <div
            className={`absolute left-0 right-0 px-4 flex items-center pointer-events-auto transition-opacity duration-300 ${showCommentsSection ? "opacity-0 pointer-events-none" : "opacity-100"}`}
            style={{
              bottom: bottomInset,
              height: progressLaneHeight,
              ...playerProgressCssVars(progressSettings),
            }}
          >
            <Slider
              variant="player"
              value={[currentTime]}
              max={duration || 100}
              step={0.1}
              onValueChange={handleSeek}
              className="w-full"
            />
          </div>


          {/* Right action buttons - TikTok style - slightly smaller */}
          <div
            className={`absolute right-1 flex flex-col gap-2 pointer-events-auto transition-opacity duration-300 ${showCommentsSection ? "opacity-0 pointer-events-none" : "opacity-100"}`}
            style={{
              bottom: `calc(${progressLaneHeight} + 0.75rem + ${bottomInset})`,
            }}
          >
            <button className="flex flex-col items-center" onClick={toggleLike}>
              <div className={`rounded-full p-2.5 ${isLiked ? "bg-primary" : "bg-player-surface/80"}`}>
                <Heart className={`h-6 w-6 ${isLiked ? "fill-current text-player-foreground" : "text-player-foreground"}`} />
              </div>
              <span className="text-player-foreground text-xs font-semibold mt-1">{likesCount}</span>
            </button>

            <button className="flex flex-col items-center" onClick={toggleCommentsSection}>
              <div className="rounded-full p-2.5 bg-player-surface/80">
                <MessageCircle className="h-6 w-6 text-player-foreground" />
              </div>
              <span className="text-player-foreground text-xs font-semibold mt-1">{commentsCount}</span>
            </button>

            <button className="flex flex-col items-center" onClick={handleShare}>
              <div className="rounded-full p-2.5 bg-player-surface/80">
                <Share2 className="h-6 w-6 text-player-foreground" />
              </div>
              <span className="text-player-foreground text-xs font-semibold mt-1">{sharesCount}</span>
            </button>

            <button className="flex flex-col items-center" onClick={toggleSave}>
              <div className={`rounded-full p-2.5 ${isSaved ? "bg-primary" : "bg-player-surface/80"}`}>
                <Bookmark className={`h-6 w-6 ${isSaved ? "fill-current text-player-foreground" : "text-player-foreground"}`} />
              </div>
            </button>

            {isOwner && (
              <>
                <button className="flex flex-col items-center" onClick={() => setShowEditSection(true)}>
                  <div className="rounded-full p-2.5 bg-secondary">
                    <Pencil className="h-6 w-6 text-player-foreground" />
                  </div>
                </button>
                <button className="flex flex-col items-center" onClick={() => setShowBoostSection(true)}>
                  <div className="rounded-full p-2.5 bg-accent">
                    <Zap className="h-6 w-6 text-player-foreground" />
                  </div>
                </button>
              </>
            )}
          </div>

          {/* Édition : formulaire unique partagé avec la liste du profil. */}
          {showEditSection && (
            <div className="pointer-events-auto">
              <VideoEditDialog
                open={showEditSection}
                onOpenChange={setShowEditSection}
                videoId={video.id}
                onSuccess={() => {
                  setShowEditSection(false);
                  onRefresh?.();
                }}
              />
            </div>
          )}

          {/* Boost : achat en Coco + durée, règles appliquées côté serveur */}
          {showBoostSection && (
            <div className="pointer-events-auto">
              <VideoBoostDialog
                open={showBoostSection}
                onOpenChange={setShowBoostSection}
                videoId={video.id}
                onBoosted={() => {
                  // Rechargement du feed uniquement si l'utilisateur est en tête
                  // de liste : on ne téléporte jamais le scroll en cours.
                  if (onRefresh && index === 0) onRefresh();
                }}
              />
            </div>
          )}

          {/* Expanded Description Overlay */}
          {showExpandedDescription && video.description && (
            <div
              className="absolute inset-0 pointer-events-auto z-50 animate-in fade-in duration-200"
              onClick={() => setShowExpandedDescription(false)}
            >
              {/* Background overlay - 60% black transparent */}
              <div className="absolute inset-0 bg-player-overlay/60" />
              
              {/* Description container */}
              <div
                className="absolute left-0 right-0 top-0 md:top-auto overflow-y-auto"
                style={{ bottom: bottomInset }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="min-h-full md:min-h-0 flex flex-col justify-end p-4">
                  {/* Close button for mobile */}
                  <button
                    onClick={() => setShowExpandedDescription(false)}
                    className="absolute top-4 right-4 text-player-muted-foreground hover:text-player-foreground md:hidden"
                  >
                    <X className="h-6 w-6" />
                  </button>
                  
                  <div className="bg-player-surface/80 rounded-lg p-4 max-h-[70vh] md:max-h-[50vh] overflow-y-auto">
                    {video.title && (
                      <h3 className="font-semibold text-player-foreground text-lg mb-2">{video.title}</h3>
                    )}
                    <p className="text-player-foreground/90 text-sm whitespace-pre-wrap leading-relaxed">
                      {video.description}
                    </p>
                    
                    {/* Tags in expanded view */}
                    {video.tags && video.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-4">
                        {video.tags.map((tag) => (
                          <Badge
                            key={tag.id}
                            variant="secondary"
                            className={tagChipClassName(tag, "text-xs player-tag-chip cursor-pointer transition-colors")}
                            onClick={() => {
                              setShowExpandedDescription(false);
                              navigate(`/search?q=${encodeURIComponent(tag.name)}`);
                            }}
                          >
                            <Hash className="h-3 w-3 mr-0.5" />
                            {tag.name}
                          </Badge>
                        ))}
                      </div>
                    )}
                    
                    <button
                      onClick={() => setShowExpandedDescription(false)}
                      className="mt-4 text-player-muted-foreground hover:text-player-foreground text-sm font-medium"
                    >
                      Réduire
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Collapsible Comments Section - Full Screen */}
      {showCommentsSection && (
        <div
          className="absolute left-0 right-0 bg-card pointer-events-auto z-50 flex flex-col animate-in slide-in-from-bottom duration-300"
          onTouchStart={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
          onTouchEnd={(e) => e.stopPropagation()}
          onWheel={(e) => e.stopPropagation()}
          onScroll={(e) => e.stopPropagation()}
          style={{
            top: "30dvh",
            bottom: "var(--player-bottom-inset)",
          }}
        >

          {/* Header avec bouton pour rétracter */}
          <div className="flex-shrink-0 flex items-center justify-between p-3 border-b border-border bg-card">
            <h3 className="text-foreground font-semibold text-base">Commentaires ({commentsCount})</h3>
            <button onClick={closeCommentsSection} className="text-muted-foreground hover:text-foreground transition-colors">
              <ChevronDown className="h-5 w-5" />
            </button>
          </div>

          {/* Formulaire de commentaire */}
          <div className="flex-shrink-0 p-3 border-b border-border bg-card">
            <form onSubmit={handleComment} className="flex gap-2">
              <Input
                ref={commentInputRef}
                placeholder="Ajouter un commentaire..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                disabled={sendingComment}
                className="bg-muted border-border text-foreground placeholder:text-muted-foreground h-9"
              />
              <Suspense fallback={<span className="flex h-9 w-9 shrink-0 items-center justify-center text-muted-foreground"><Smile className="h-5 w-5" /></span>}>
                <CommentEmojiButton inputRef={commentInputRef} value={newComment} onChange={setNewComment} disabled={sendingComment} />
              </Suspense>
              <Button type="submit" size="icon" disabled={sendingComment} className="bg-primary text-primary-foreground hover:bg-primary/90 h-9 w-9">
                <Send className="h-4 w-4" />
              </Button>
            </form>
            {sendingComment && commentAutoModeration && (
              <p role="status" className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Analyse du contenu en cours…
              </p>
            )}
          </div>

          {/* Liste des commentaires avec scroll - occupe tout l'espace restant */}
          <div
            style={{
              WebkitOverflowScrolling: "touch",
              // Le scroll de la liste ne doit jamais « déborder » sur le feed.
              overscrollBehavior: "contain",
              touchAction: "pan-y",
            }}
            className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4 bg-card"
          >

            {comments.map((comment) => (
              <div key={comment.id} className="flex gap-3">
                <Avatar
                  className="h-10 w-10 cursor-pointer flex-shrink-0"
                  onClick={() => comment.profiles && navigate(profilePath(comment.profiles))}
                >
                  <AvatarImage src={comment.profiles?.avatar_url || undefined} />
                  <AvatarFallback className="bg-secondary text-secondary-foreground text-sm">
                    {comment.profiles?.full_name?.[0] || "U"}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p
                      className="text-sm font-semibold text-foreground cursor-pointer hover:underline"
                      onClick={() => comment.profiles && navigate(profilePath(comment.profiles))}
                    >
                      {comment.profiles?.full_name || "Utilisateur"}
                    </p>
                    <p className="text-xs text-muted-foreground">{new Date(comment.created_at!).toLocaleDateString()}</p>

                    {/* Menu d'actions : auteur = modifier/supprimer, sinon signaler */}
                    {user && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            aria-label="Actions sur le commentaire"
                            className="ml-auto text-muted-foreground hover:text-foreground transition-colors"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {comment.user_id === user.id ? (
                            <>
                              <DropdownMenuItem onClick={() => startEditComment(comment.id, comment.content)}>
                                <Pencil className="h-4 w-4 mr-2" />
                                Modifier
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setCommentToDelete(comment.id)}>
                                <Trash2 className="h-4 w-4 mr-2" />
                                Supprimer
                              </DropdownMenuItem>
                            </>
                          ) : (
                            <DropdownMenuItem onClick={() => setCommentToReport(comment.id)}>
                              <Flag className="h-4 w-4 mr-2" />
                              Signaler le commentaire
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>

                  {editingCommentId === comment.id ? (
                    <div className="mb-2 space-y-2">
                      <div className="flex gap-2">
                        <Input
                          ref={editCommentInputRef}
                          value={editingCommentContent}
                          onChange={(e) => setEditingCommentContent(e.target.value)}
                          disabled={savingComment}
                          className="bg-muted border-border text-foreground h-9"
                          autoFocus
                        />
                        <Suspense fallback={null}>
                          <CommentEmojiButton inputRef={editCommentInputRef} value={editingCommentContent} onChange={setEditingCommentContent} disabled={savingComment} />
                        </Suspense>
                      </div>
                      {savingComment && commentAutoModeration && (
                        <p role="status" className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          Analyse du contenu en cours…
                        </p>
                      )}
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => saveEditedComment(comment.id)}
                          disabled={savingComment}
                        >
                          Enregistrer
                        </Button>
                        <Button size="sm" variant="outline" onClick={cancelEditComment} disabled={savingComment}>
                          Annuler
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-foreground mb-2 leading-relaxed">{comment.content}</p>
                  )}


                  {/* Bouton like */}
                  <button
                    onClick={() => toggleCommentLike(comment.id)}
                    className="flex items-center gap-1.5 text-muted-foreground hover:text-red-500 transition-colors"
                  >
                    <Heart
                      className={`h-4 w-4 ${commentLikes[comment.id]?.isLiked ? "fill-red-500 text-red-500" : ""}`}
                    />
                    <span className="text-xs font-medium">{commentLikes[comment.id]?.count || 0}</span>
                  </button>
                </div>
              </div>
            ))}
            {comments.length === 0 && (
              <p className="text-center text-muted-foreground text-sm py-8">Aucun commentaire pour le moment</p>
            )}
          </div>

          {/* Signalement : en bas de la feuille commentaires, hors des icônes du lecteur */}
          <div className="flex-shrink-0 p-3 border-t border-border bg-card">
            <button
              onClick={() => setShowReportDialog(true)}
              className="w-full bg-red-500/10 hover:bg-red-500/20 text-red-600 text-sm py-2 rounded-lg flex items-center justify-center gap-2 transition-colors"
            >
              <Flag className="h-4 w-4" />
              <span className="font-medium">Signaler cette vidéo</span>
            </button>
          </div>
        </div>
      )}

      <ReportDialog
        open={showReportDialog}
        onOpenChange={setShowReportDialog}
        contentType="video"
        contentId={video.id}
      />

      {/* Signalement d'un commentaire */}
      {commentToReport && (
        <ReportDialog
          open
          onOpenChange={(open) => {
            if (!open) setCommentToReport(null);
          }}
          contentType="comment"
          contentId={commentToReport}
        />
      )}

      {/* Confirmation de suppression d'un commentaire */}
      <AlertDialog
        open={!!commentToDelete}
        onOpenChange={(open) => {
          if (!open) setCommentToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce commentaire ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est définitive : le commentaire ne pourra pas être récupéré.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const id = commentToDelete;
                setCommentToDelete(null);
                if (id) deleteComment(id);
              }}
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
};
export default VideoPlayer;
